import type { ApiClient } from "./client";
import type { AppointmentChecklist } from "./evidence";

export type ChecklistTask = { id: string; title: string };

export type ChecklistCategory = { id: string; title: string; tasks?: ChecklistTask[] };

export type ChecklistTaskAnswer = { id: string; appointment: string; user: string | null; task: string; done: boolean; created_at: string; updated_at: string };

export type ChecklistState = { serviceTasks: ChecklistCategory[]; appointmentTasks: ChecklistTaskAnswer[] };

export const EMPTY_CHECKLIST: ChecklistState = { serviceTasks: [], appointmentTasks: [] };

function categoriesOf(items: AppointmentChecklist["items"]): ChecklistCategory[] {
  const categories = new Map<string, { id: string; title: string; tasks: ChecklistTask[] }>();
  for (const { category, task } of items) {
    const grouped = categories.get(category.id) ?? { id: category.id, title: category.name, tasks: [] };
    grouped.tasks.push({ id: task.id, title: task.name });
    categories.set(category.id, grouped);
  }
  return [...categories.values()];
}

export function toChecklistState(appointmentId: string, { items }: AppointmentChecklist): ChecklistState {
  return {
    serviceTasks: categoriesOf(items),
    appointmentTasks: items.flatMap(({ task, done, answer }) =>
      answer === null ? [] : [{ id: answer.id, appointment: appointmentId, user: answer.by?.id ?? null, task: task.id, done, created_at: answer.at, updated_at: answer.at }],
    ),
  };
}

const sameCategories = (shown: ChecklistCategory[], answered: ChecklistCategory[]): boolean =>
  JSON.stringify(shown) === JSON.stringify(answered);

const tasksOn = (categories: ChecklistCategory[]): Set<string> => new Set(categories.flatMap((category) => (category.tasks ?? []).map((task) => task.id)));

const newer = (shown: ChecklistTaskAnswer | undefined, answered: ChecklistTaskAnswer | undefined): ChecklistTaskAnswer | undefined => {
  if (shown === undefined) return answered;
  if (answered === undefined) return shown;
  return shown.updated_at > answered.updated_at ? shown : answered;
};

export function newerChecklist(shown: ChecklistState, answered: ChecklistState): ChecklistState {
  const shownByTask = new Map(shown.appointmentTasks.map((answer) => [answer.task, answer]));
  const answeredByTask = new Map(answered.appointmentTasks.map((answer) => [answer.task, answer]));
  const serviceTasks = sameCategories(shown.serviceTasks, answered.serviceTasks) ? shown.serviceTasks : answered.serviceTasks;
  return {
    serviceTasks,
    appointmentTasks: [...tasksOn(answered.serviceTasks)].flatMap((task) => newer(shownByTask.get(task), answeredByTask.get(task)) ?? []),
  };
}

type ChecklistApi = Pick<ApiClient["appointments"], "checklist" | "tick">;

export function createAppointmentChecklist(api: ChecklistApi) {
  return {
    read: async (appointmentId: string): Promise<ChecklistState> => toChecklistState(appointmentId, await api.checklist(appointmentId)),
    tick: async (appointmentId: string, task: string, done: boolean): Promise<ChecklistState> => toChecklistState(appointmentId, await api.tick(appointmentId, task, { done })),
  };
}
